import { Navigate, Route, Routes } from 'react-router';
import {
  LoginScreen,
  SetPasswordScreen,
  ForgotPasswordScreen,
  ForgotPasswordManualEntryScreen,
  ResetPasswordScreen,
} from './pods/auth';
import { useTypedSelector } from './reducers';
import { useIsAuthenticated } from './hooks';
import { DashboardLayout } from './components/dashboard';
import {
  LibraryListScreen,
  NewLibraryScreen,
  EditLibraryScreen,
  ContentTagsListScreen,
  NewContentTagScreen,
  EditContentTagScreen,
  ProgramOverviewScreen,
  ProgramWeeksScreen,
  ProgramWeekScreen,
  CreateDailyTipScreen,
  EditDailyTipScreen,
  CreateWeeklyTaskScreen,
  EditWeeklyTaskScreen,
  CreatePsychologyPromptScreen,
  EditPsychologyPromptScreen,
  CreateReflectionPointScreen,
  EditReflectionPointScreen,
  CreateLessonScreen,
  EditLessonScreen,
} from './pods/content';
import {
  AppUsersListScreen,
  ClientsListScreen,
  UserDetailsTabsScreen,
  UserFeedbackListScreen,
  UserClassificationsListScreen,
  NewUserClassificationScreen,
  EditUserClassificationScreen,
  ClinicianListScreen,
  NewClinicianScreen,
  EditClinicianScreen,
  ClinicianAssignedUsers,
  ClinicianAppointments,
  ChatScreen,
  ChatDetail,
  AppointmentsListScreen,
  EditScheduleScreen,
  MyEditScheduleScreen,
} from './pods/app';
import {
  UserMetricsScreen,
  ProgramProgressScreen,
  QuestionnaireResponses,
  ContentInfo,
} from './components';
import {
  B2BOrganisationsListScreen,
  NewB2BOrganisationScreen,
  EditB2BOrganisationScreen,
  B2BOrganisationDetailsScreen,
  TeamDetailsScreen,
  B2BTeamsListScreen,
} from './pods/b2b';

export default function RootNavigator() {
  const { user, viewMode } = useTypedSelector((state) => state.auth);
  const isAuthenticated = useIsAuthenticated();

  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/set-password" element={<SetPasswordScreen />} />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route
        path="/forgot-password/manual-entry"
        element={<ForgotPasswordManualEntryScreen />}
      />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />
      {isAuthenticated && (
        <Route element={<DashboardLayout viewMode={viewMode} />}>
          <Route path="/library" element={<LibraryListScreen />} />
          <Route path="/library/new" element={<NewLibraryScreen />} />
          <Route path="/library/:id" element={<EditLibraryScreen />} />
          <Route
            path="/library/content-tags"
            element={<ContentTagsListScreen />}
          />
          <Route
            path="/library/content-tags/new"
            element={<NewContentTagScreen />}
          />
          <Route
            path="/library/content-tags/:id"
            element={<EditContentTagScreen />}
          />
        </Route>
      )}
      {isAuthenticated && viewMode === 'admin' && (
        <Route element={<DashboardLayout viewMode="admin" />}>
          <Route path="/program" element={<ProgramOverviewScreen />} />
          <Route path="/program/:programId" element={<ProgramWeeksScreen />} />
          <Route
            path="/program/:weekNumber/:weekId"
            element={<ProgramWeekScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/daily-tips"
            element={<ProgramWeekScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/weekly-tasks"
            element={<ProgramWeekScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/psychology-prompts"
            element={<ProgramWeekScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/lessons"
            element={<ProgramWeekScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/reflection-points"
            element={<ProgramWeekScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/daily-tips/new"
            element={<CreateDailyTipScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/daily-tips/:id"
            element={<EditDailyTipScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/weekly-tasks/new"
            element={<CreateWeeklyTaskScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/weekly-tasks/:id"
            element={<EditWeeklyTaskScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/psychology-prompts/new"
            element={<CreatePsychologyPromptScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/psychology-prompts/:id"
            element={<EditPsychologyPromptScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/reflection-points/new"
            element={<CreateReflectionPointScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/reflection-points/:id"
            element={<EditReflectionPointScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/lessons/new"
            element={<CreateLessonScreen />}
          />
          <Route
            path="/program/:weekNumber/:weekId/lessons/:id"
            element={<EditLessonScreen />}
          />

          <Route path="/users" element={<AppUsersListScreen />} />
          <Route path="/users/:id" element={<UserDetailsTabsScreen />} />
          <Route
            path="/users/:id/account-details"
            element={<UserDetailsTabsScreen />}
          />
          <Route
            path="/users/:id/user-dashboard"
            element={<UserDetailsTabsScreen />}
          />
          <Route
            path="/users/:id/metrics/:tab"
            element={<UserMetricsScreen />}
          />
          <Route
            path="/users/:id/program"
            element={<ProgramProgressScreen />}
          />
          <Route
            path="/users/:id/program/:programId"
            element={<ProgramProgressScreen />}
          />
          <Route
            path="/users/:id/questionnaire-responses"
            element={<QuestionnaireResponses />}
          />
          <Route
            path="/users/:id/questionnaire-responses/onboarding"
            element={<QuestionnaireResponses />}
          />
          <Route
            path="/users/:id/questionnaire-responses/pre-health-coach"
            element={<QuestionnaireResponses />}
          />
          <Route path="/users/:id/content-info" element={<ContentInfo />} />

          <Route
            path="/user-classifications"
            element={<UserClassificationsListScreen />}
          />
          <Route
            path="/user-classifications/new"
            element={<NewUserClassificationScreen />}
          />
          <Route
            path="/user-classifications/:id"
            element={<EditUserClassificationScreen />}
          />

          <Route path="/clinicians" element={<ClinicianListScreen />} />
          <Route path="/clinicians/new" element={<NewClinicianScreen />} />
          <Route path="/clinicians/:id" element={<EditClinicianScreen />} />
          <Route
            path="/clinicians/:id/assigned-users"
            element={<ClinicianAssignedUsers />}
          />
          <Route
            path="/clinicians/:id/appointments"
            element={<ClinicianAppointments />}
          />
          <Route
            path="/clinicians/:id/edit-schedule"
            element={<EditScheduleScreen />}
          />

          <Route path="/feedback" element={<UserFeedbackListScreen />} />

          <Route path="/b2b" element={<B2BOrganisationsListScreen />} />
          <Route path="/b2b/new" element={<NewB2BOrganisationScreen />} />
          <Route path="/b2b/:id" element={<B2BOrganisationDetailsScreen />} />
          <Route path="/b2b/:id/edit" element={<EditB2BOrganisationScreen />} />
          <Route path="/b2b/:id/team/:teamId" element={<TeamDetailsScreen />} />
        </Route>
      )}
      {isAuthenticated && viewMode === 'clinician' && (
        <Route element={<DashboardLayout viewMode="clinician" />}>
          <Route path="/users" element={<ClientsListScreen />} />
          <Route path="/users/:id" element={<UserDetailsTabsScreen />} />
          <Route
            path="/users/:id/user-dashboard"
            element={<UserDetailsTabsScreen />}
          />
          <Route
            path="/users/:id/metrics/:tab"
            element={<UserMetricsScreen />}
          />
          <Route
            path="/users/:id/program"
            element={<ProgramProgressScreen />}
          />
          <Route
            path="/users/:id/program/:programId"
            element={<ProgramProgressScreen />}
          />
          <Route
            path="/users/:id/questionnaire-responses"
            element={<QuestionnaireResponses />}
          />
          <Route
            path="/users/:id/questionnaire-responses/pre-health-coach"
            element={<QuestionnaireResponses />}
          />
          <Route
            path="/users/:id/questionnaire-responses/onboarding"
            element={<QuestionnaireResponses />}
          />
          <Route path="/users/:id/content-info" element={<ContentInfo />} />

          <Route
            path="/chat"
            element={
              <Navigate
                to={
                  user?.roles.includes('ROLE_COACH')
                    ? '/chat/active'
                    : '/chat/broadcast'
                }
                replace
              />
            }
          />
          <Route path="/chat/active" element={<ChatScreen />} />
          <Route path="/chat/broadcast" element={<ChatScreen />} />
          <Route path="/chat/archived" element={<ChatScreen />} />
          <Route path="/chat/:type/:userId" element={<ChatDetail />} />
          <Route path="/chat/broadcast/user/:userId" element={<ChatDetail />} />
          <Route path="/chat/broadcast/team/:teamId" element={<ChatDetail />} />
          <Route path="/appointments" element={<AppointmentsListScreen />} />
          <Route
            path="/appointments/edit-schedule"
            element={<MyEditScheduleScreen />}
          />

          <Route path="/b2b-teams" element={<B2BTeamsListScreen />} />

          <Route path="*" element={<Navigate to="/users" />} />
        </Route>
      )}
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
}
